import { Collapsible } from '@base-ui/react/collapsible';
import { useRender } from '@base-ui/react/use-render';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import type { HTMLAttributes, ReactElement } from 'react';
import { Checkbox, type CheckboxProps } from '../primitives/checkbox.tsx';
import { cn } from '../util/classes.ts';
import { Container, type ContainerProps } from './container.tsx';

export type CardProps = ContainerProps & {
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    highlighted?: boolean;
};

export function Card({
    className,
    level,
    minLevel = 2,
    nestLevel = 1,
    open,
    defaultOpen,
    onOpenChange,
    highlighted = false,
    render,
    children,
    ...props
}: CardProps): ReactElement {
    return (
        <Collapsible.Root
            open={open}
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
            render={
                <Container
                    render={render}
                    className={cn(
                        'flex flex-col items-justify relative',
                        // container queries drive the header's narrow layout
                        '@container',
                        'transition duration-state',
                        'l2:bg-container l2:rounded-default l2:border l2:border-line-weak',
                        // a boxless row tints while open, as a rounded tile
                        'l3:rounded-tight l3:card-expanded:bg-container-hover',
                        // A highlighted card is one `data-highlighted` away from a normal
                        // one, so `card-highlighted:` styles it the way `card-completed:`
                        // and `card-expanded:` style the other card states — including from
                        // a descendant, which a prop alone could not reach.
                        'l2:card-highlighted:bg-container-highlight',
                        'has-data-[slot=card-main-action]:cursor-pointer',
                        // a card that opens something darkens its outline, a card
                        // that folds open tints its fill
                        'l2:card-link-hover:border-line-strong',
                        'l2l3:has-[[data-slot=card-main-action][aria-expanded]]:hover:bg-container-hover',
                        'l2l3:card-highlighted:has-[[data-slot=card-main-action][aria-expanded]]:hover:bg-container-highlight-hover',
                        // joined cards share one hairline: each overlaps the card
                        // above and leaves its own top border unpainted
                        'l2:joined-vertical:bg-clip-padding',
                        'l2:joined-vertical-leading:rounded-b-none',
                        'l2:joined-vertical-trailing:rounded-t-none l2:joined-vertical-trailing:-mt-px',
                        'l2:joined-vertical-trailing:border-t-transparent',
                        // the hovered card repaints that seam and rises above its
                        // neighbours; no fade, which would flash the shared edge
                        'l2:joined-vertical-trailing:card-link-hover:border-t-line-strong',
                        'l2:joined-vertical:card-link-hover:z-1',
                        'l2:joined-vertical:transition-[background-color]',
                        'card-interactive-child:relative card-interactive-child:z-1',
                        // a flex title shows no ellipsis, so `truncate` on one makes it a block
                        '[&_[data-slot=card-title].truncate]:block',
                        className,
                    )}
                    level={level}
                    minLevel={minLevel}
                    nestLevel={nestLevel}
                    data-slot="card"
                    data-highlighted={highlighted || undefined}
                    {...props}
                />
            }
        >
            {children}
        </Collapsible.Root>
    );
}

export type CardActionsProps = HTMLAttributes<HTMLDivElement>;

export function CardActions({ className, children, ...props }: CardActionsProps): ReactElement {
    // we use another container here to drop the actions down a level in the hierarchy
    return (
        <Container
            data-slot="card-actions"
            className={cn(
                'card-expanded:border-line-weak card-expanded:not-card-completed:border-t',
                'card-completed:card-expanded:grid-rows-[0fr]',
                className,
            )}
            {...props}
            render={<CardExpandContent gutter={false}>{children}</CardExpandContent>}
        />
    );
}

export type CardActionsGroupProps = useRender.ComponentProps<'div'>;

export function CardActionsGroup({ className, render, ...props }: CardActionsGroupProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            ...props,
            className: cn('flex flex-wrap items-center gap-tight', className),
            'data-slot': 'card-actions-group',
        },
    });
}

export type CardCompleteProps = CheckboxProps;

export function CardComplete({ className, ...props }: CardCompleteProps): ReactElement {
    // centred on the 20px first line, like CardStart's icon
    return <Checkbox data-slot="card-complete" className={cn('my-0.5', className)} {...props} />;
}

export type CardContentProps = useRender.ComponentProps<'div'>;

export function CardContent({ className, render, ...props }: CardContentProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            'data-slot': 'card-content',
            ...props,
            // min-w-0 lets truncating rows shrink instead of pushing the card wider
            className: cn('grow min-w-0 flex flex-col gap-container-cozy', className),
        },
    });
}

export type CardDetailProps = useRender.ComponentProps<'div'>;

export function CardDetail({ className, render, ...props }: CardDetailProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            'data-slot': 'card-detail',
            ...props,
            // wrap-anywhere also shrinks min-content, so unbroken text can't widen the card
            className: cn('min-w-0 wrap-anywhere text-content-weaker', className),
        },
    });
}

export type CardEndProps = useRender.ComponentProps<'div'>;

export function CardEnd({ className, render, ...props }: CardEndProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: { 'data-slot': 'card-end', ...props, className: cn('shrink-0 text-content-weak', className) },
    });
}

export type CardExpandActionProps = Collapsible.Trigger.Props;

export function CardExpandAction(props: CardExpandActionProps): ReactElement {
    return (
        <Collapsible.Trigger
            nativeButton={false}
            {...props}
            render={
                <CardMainAction className="flex items-center">
                    <ChevronRight className="size-4 transition-transform duration-state card-expanded:rotate-90" />
                </CardMainAction>
            }
        />
    );
}

export type CardExpandContentProps = Collapsible.Panel.Props & {
    gutter?: boolean;
};

export function CardExpandContent({
    className,
    children,
    gutter = true,
    ...props
}: CardExpandContentProps): ReactElement {
    return (
        <Collapsible.Panel
            keepMounted
            data-slot="card-panel"
            data-gutter={gutter}
            {...props}
            className={cn(
                // closed = display:none; data-starting/ending-style are the
                // visible frames that let the height transition run
                'h-(--collapsible-panel-height) overflow-hidden',
                // surface motion: in 320 / out 200 (Dialog recipe)
                'transition-[height,opacity] ease-standard duration-interactive',
                'data-ending-style:duration-state',
                'data-starting-style:h-0 data-starting-style:opacity-0',
                'data-ending-style:h-0 data-ending-style:opacity-0',
                'data-[gutter=true]:px-[calc(var(--spacing-container)+16px)]',
                className,
            )}
        >
            <div className="flex p-container gap-container">{children}</div>
        </Collapsible.Panel>
    );
}

export type CardFrontProps = useRender.ComponentProps<'div'>;

export function CardFront({ className, render, ...props }: CardFrontProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            ...props,
            className: cn(
                'flex l2:rounded-default',
                // follow the box's own squared-off corners when joined
                'l2:joined-vertical-leading:rounded-b-none l2:joined-vertical-trailing:rounded-t-none',
                'p-container gap-container',
                'card-completed:line-through card-completed:text-content-weak',
                className,
            ),
            'data-slot': 'card-front',
        },
    });
}

export type CardHeaderProps = useRender.ComponentProps<'div'>;

export function CardHeader({ className, render, ...props }: CardHeaderProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            ...props,
            // narrow: caption-sized subtitle stacks above the title; one
            // breakpoint flips placement and size together
            className: cn(
                'grow flex flex-col gap-0.5',
                '@md:flex-row @md:items-baseline @md:gap-container',
                className,
            ),
            'data-slot': 'card-header',
        },
    });
}

export type CardOpenIconProps = HTMLAttributes<HTMLElement | SVGSVGElement>;

export function CardOpenIcon({ className, ...props }: CardOpenIconProps): ReactElement {
    return (
        <ArrowUpRight
            className={cn('size-4 transition-colors duration-state card-link-hover:text-content', className)}
            {...props}
        />
    );
}

export type CardMainActionProps = useRender.ComponentProps<'div'>;

export function CardMainAction({ className, render, ...props }: CardMainActionProps): ReactElement {
    return (
        <CardEnd
            className={cn(
                'cursor-pointer p-0 appearance-none flex items-center',
                // this is a hack to make the whole card clickable without
                // having invalid dom nesting for cards with other
                // interactive components within them
                'after:absolute after:inset-0',
                // an empty action is only the overlay; cancel the gap it would add
                'empty:-ms-container',
                // the focus ring goes round the overlay, so the whole card shows it
                'outline-none after:rounded-default focus-visible:after:outline-1 focus-visible:after:outline-(--color-main)',
                className,
            )}
            data-slot="card-main-action"
            // the above hack doesn't work on actual buttons
            role={render ? undefined : 'button'}
            tabIndex={render ? undefined : 0}
            onKeyDown={
                render
                    ? undefined
                    : (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              event.currentTarget.click();
                          }
                      }
            }
            {...props}
            render={render}
        />
    );
}

export type CardStartProps = useRender.ComponentProps<'div'>;

export function CardStart({ className, render, ...props }: CardStartProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            'data-slot': 'card-start',
            ...props,
            className: cn(
                'shrink-0 text-content-weak',
                // bare icons fixed at 16px; self form unguarded (override
                // with !size-*), child form yields to an explicit size-*
                "[svg]:size-4 *:[svg:not([class*='size-'])]:size-4",
                '[svg]:box-content',
                // centres the 16px icon on the 20px first line
                'my-0.5',
                'transition-colors duration-state card-link-hover:text-content',
                className,
            ),
        },
    });
}

export type CardSubtitleProps = useRender.ComponentProps<'div'>;

export function CardSubtitle({ className, render, ...props }: CardSubtitleProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            'data-slot': 'card-subtitle',
            ...props,
            className: cn(
                'flex max-w-full shrink-0 items-center gap-tight whitespace-nowrap text-content-weaker',
                'order-first text-ui-sm @md:order-0 @md:text-ui-base',
                className,
            ),
        },
    });
}

export type CardTitleProps = useRender.ComponentProps<'div'>;

export function CardTitle({ className, render, ...props }: CardTitleProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            'data-slot': 'card-title',
            ...props,
            // wrap-anywhere also shrinks min-content, so unbroken text can't widen the card
            className: cn('flex gap-tight grow min-w-0 wrap-anywhere card-link-hover:underline', className),
        },
    });
}
